import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Toast from "light-toast";
import Cookies from "js-cookie";
import api from "@/common/net/apis";
import "@/common/sensor";
import type {
  FifaCouponBindingItem,
  FifaCouponCard,
  FifaGetCouponPageConfig,
  FifaUiConfig,
  RouterParams,
} from "@/pages/@types/FifaGetCoupon";
import { FifaBindRetFlag } from "@/pages/@types/FifaGetCoupon";
import "@/assets/styles/fifaGetCoupon.less";
import {
  ANIMATION,
  CardType,
  IMAGES,
  RATIOS,
  ReceiveStatus,
  preloadImages,
} from "./contants";
import debounce from "lodash/debounce";
import { getCt, getEnv } from "@/utils/common";
import { usePreventTouchRubberBand } from "@/hooks/usePreventTouchRubberBand";

const FifaGetCoupon = () => {
  const [stopGifAnimation, setStopGifAnimation] = useState(false);
  const [showCardView, setShowCardView] = useState(false);
  const [reachedBottom, setReachedBottom] = useState("");
  const [pageConfig, setPageConfig] = useState<FifaGetCouponPageConfig | null>(null);
  const [showEmpty, setShowEmpty] = useState(false);
  const isShowBottomShadow = useRef(true);
  const fifaIndexMetaRef = useRef<{ pageKey: string; rawCouponList: FifaCouponCard[] }>({
    pageKey: "",
    rawCouponList: [],
  });
  const mountedRef = useRef(false);
  const uiTimeoutIdsRef = useRef<number[]>([]);

  const scheduleDelayedUiUpdate = useCallback((run: () => void, delayMs: number) => {
    const id = window.setTimeout(() => {
      uiTimeoutIdsRef.current = uiTimeoutIdsRef.current.filter((t) => t !== id);
      if (!mountedRef.current) return;
      run();
    }, delayMs);
    uiTimeoutIdsRef.current.push(id);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      uiTimeoutIdsRef.current.forEach((timerId) => window.clearTimeout(timerId));
      uiTimeoutIdsRef.current = [];
    };
  }, []);

    /**
   * iOS/微信 WebView 的“橡皮筋回弹/整页跟手滑动”处理（同 lanternVerUpdate）
   * - 让页面根容器承担滚动（overflow-y: auto）
   * - 在“顶端继续下拉 / 底部继续上拉”时阻止默认行为，避免回弹
   */
  usePreventTouchRubberBand('fifaCouponWrapper');

  const routerParams = useMemo<RouterParams>(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return {
      matchId: searchParams.get("matchId") || "",
      teamId: searchParams.get("teamId") || "",
      pageSource: searchParams.get("page_source"),
      sid: searchParams.get("sid"),
    };
  }, []);

  const pageViewTrack = useCallback((flag = 1) => {
    (window as any).sensors?.track("customizePageView", {
      page_source:"麦麦日历",
      belong_page: "补麦时刻限时领券页",
      promotion_id: "mds领券",
    });
  }, []);

  const btnClickTrack = () => {
    (window as any).sensors?.track("buttonClick", {
      page_source:"麦麦日历",
      belong_page: "补麦时刻限时领券页",
      button_name: pageConfig?.status === ReceiveStatus.RECEIVED_SUCCESS ? "去使用" : "去领取",
      promotion_id: "mds领券",
    });
  };

  const matchOriginalByReceiveId = (list: FifaCouponCard[], receiveId: string | number | undefined) => {
    if (!receiveId) return;
    const key = String(receiveId);
    return list.find((o) => o.receiveId != null && String(o.receiveId) === key);
  };
  
  /** binding 券明细不含 price / tradeStartTime / tradeEndTime，仅从首页 couponList 合并展示字段 */
  const mapBindingSuccessToLegacyCards = (
    bindingItems: FifaCouponBindingItem[],
    originalList: FifaCouponCard[]
  ): FifaCouponCard[] => {
    const successes = bindingItems.filter((item) => item.success);
    const isMultiple = successes.length > 1;
    return successes.map((item) => {
      const orig =
        matchOriginalByReceiveId(originalList, item.receiveId) || {};
      return {
        ...orig,
        couponName: item.couponName || orig.couponName,
        couponPic: orig.singleStyleImg || "",
        couponLogo: isMultiple ? orig.multiStyleImg : orig.singleStyleImg || "",
      };
    });
  };
  
  /** 根据页面 status 选择主操作按钮图（status 来自首页 claimButton.status，领取后会按 binding 结果更新） */
  const getFifaMainButtonSrc = (ui: FifaUiConfig | null | undefined, status: number): string => {
    if (!ui) return "";
    const recv = ui.receiveButton || ui.pageButton || "";
    const succ = ui.receiveSuccessButton || ui.pageSuccessButton || recv;
    const end = ui.activityEndButton || recv;
    if (status === ReceiveStatus.RECEIVED_SUCCESS) return succ;
    if (status === ReceiveStatus.RECEIVED_FAILED) return end;
    return recv;
  };


  const toCouponList = debounce(() => {
    window.location.href =
      "mcdapp://page?iosPageName=MCDMyWalletHomeViewController&androidPageName=ComponentUser&androidPageAction=coupon_list&harmonyPageName=WalletPage&parameters=";
  }, 1000, { leading: true, trailing: false });

  const updatePageConfigByCoupons = (
    coupons: FifaCouponCard[],
    received: ReceiveStatus,
    uiConfig: FifaGetCouponPageConfig["uiConfig"]
  ) => {
    const couponLength = coupons.length;
    setPageConfig({
      uiConfig,
      status: received,
      cardType: couponLength > 1 ? CardType.MULTIPLE : CardType.SINGLE,
      multipleCards: couponLength > 1 ? coupons : null,
      singleCard: couponLength === 1 ? coupons[0] : null,
    });
  };

  /** 将首页 couponList 转换为 legacy 样式（仅保留 singleStyleImg 和 multiStyleImg） */
  const mapCouponListToLegacyCards = (couponList: FifaCouponCard[] = []) => {
    const isMultiple = couponList.length > 1;
    return couponList.map((coupon) => ({
      ...coupon,
      couponPic: coupon.singleStyleImg || "",
      couponLogo: isMultiple ? coupon.multiStyleImg : coupon.singleStyleImg || "",
    }));
  };

  const mapPageDataToLegacyUiConfig = (data: any) => {
    const receiveBtn = data?.receiveButton || "";
    const receiveSuccessBtn = data?.receiveSuccessButton || receiveBtn;
    const activityEndBtn = data?.activityEndButton || receiveBtn;

    const uiConfig: FifaUiConfig = {
      pageTitle: data?.title || "",
      pageTitleImage: data?.titleImg || "",
      pageBackground: data?.pageBackground || "",
      pageMainImage: data?.themeBackground || "",
      pageGetCoupon: data?.themeCover || "",
      receiveButton: receiveBtn,
      receiveSuccessButton: receiveSuccessBtn,
      activityEndButton: activityEndBtn,
      pageButton: receiveBtn,
      pageSuccessButton: receiveSuccessBtn,
      ruleDesc: data?.instructions || "",
    };
    return uiConfig;
  };

  /**
 * 跳转登录
 */
const toLogin = debounce(()=>{
  if(getEnv().isApp) {
    window.location.href = 'https://mcd.cn/login';
  }
}, 1000, { leading: true, trailing: false });

  const getPageConfig = useCallback(async () => {
    try {
      const sid = routerParams.sid || Cookies.get("sid") || "";
      const response: any = await api.getFifaCouponPageIndex({
        matchId: routerParams.matchId,
        teamId: routerParams.teamId,
      }, { sid });

      if (!response?.data) {
        throw new Error("Invalid response data");
      }

      const rawCouponList = (response.data?.couponList || []) as FifaCouponCard[];
      fifaIndexMetaRef.current = {
        pageKey: response.data?.pageKey ?? response.data?.page_key ?? "",
        rawCouponList,
      };
      const coupons = mapCouponListToLegacyCards(rawCouponList);
      const received = (response.data?.claimButton?.status ?? ReceiveStatus.NOT_RECEIVED) as ReceiveStatus;
      const uiConfig = mapPageDataToLegacyUiConfig(response.data);
      pageViewTrack(received);
      if (received === ReceiveStatus.RECEIVED_SUCCESS) {
        scheduleDelayedUiUpdate(() => {
          setShowCardView(true);
        }, ANIMATION.CARD_SHOW_DELAY);
        scheduleDelayedUiUpdate(() => {
          setReachedBottom("bottomShadowFadeIn");
        }, ANIMATION.BOTTOM_SHADOW_DELAY);
      }
      updatePageConfigByCoupons(coupons, received, uiConfig);
      setShowEmpty(false);
    } catch (error) {
      setPageConfig(null);
      setShowEmpty(true);
    }
  }, [pageViewTrack, routerParams.matchId, routerParams.sid, routerParams.teamId, scheduleDelayedUiUpdate]);

  const receiveCoupon = debounce(async () => {
    btnClickTrack();
    // 活动结束
    if (pageConfig?.status === ReceiveStatus.RECEIVED_FAILED) {
      return;
    }
    // 券领取成功
    if (pageConfig?.status === ReceiveStatus.RECEIVED_SUCCESS) {
      toCouponList()
      return;
    }
    // 领取券
    try {
      const sid = routerParams.sid || Cookies.get("sid") || "";
      const { pageKey, rawCouponList } = fifaIndexMetaRef.current;
      const receiveList = rawCouponList
        .filter((c) => c.receiveFlag === 0)
        .map((c) => c.receiveId)
        .filter((id): id is string => !!id);

      if (!pageKey) {
        Toast.info("页面信息异常，请刷新重试", 2000);
        return;
      }
      if (!receiveList.length) {
        Toast.info("暂无可领取的券", 2000);
        return;
      }

      const win = window as any;
      const vdataRaw = win?.SMVdataSDK?.getVdata?.();
      const bindBody: Record<string, unknown> = {
        teamId: routerParams.teamId,
        matchId: routerParams.matchId,
        pageKey,
        receiveList,
        channelCode: getCt(),
      };
      if (vdataRaw !== "" && vdataRaw != null) {
        bindBody.vdata = vdataRaw;
      }

      const response: any = await api.bindFifaCouponPage(bindBody, { sid });

      if (!response?.success) {
        Toast.info(response?.message || "活动太火爆，请稍后再试", 2000);
        return;
      }

      const bindingData = response.data;
      const retFlag = Number(bindingData?.retFlag);
      const couponResults: FifaCouponBindingItem[] = bindingData?.coupons || [];

      if (retFlag === FifaBindRetFlag.ALL_FAILED) {
        const message = bindingData?.message || "领取失败，请稍后重试";
        setPageConfig((prev: FifaGetCouponPageConfig | null) => ({
          uiConfig: prev?.uiConfig || null,
          status: ReceiveStatus.RECEIVED_FAILED,
          cardType: CardType.SINGLE,
          multipleCards: [],
          singleCard: null,
        }));
        Toast.info(message, 2000);
        return;
      } else if (retFlag === FifaBindRetFlag.PARTIAL_SUCCESS || retFlag === FifaBindRetFlag.ALL_SUCCESS) {
        const legacyCoupons = mapBindingSuccessToLegacyCards(couponResults, rawCouponList);
        if (!legacyCoupons.length) {
          Toast.info(bindingData?.message || "领取失败，请稍后重试", 2000);
          return;
        }
        Toast.info(bindingData?.message || "券领取成功", 2500);
        setStopGifAnimation(true);
        scheduleDelayedUiUpdate(() => {
          setShowCardView(true);
        }, ANIMATION.CARD_SHOW_DELAY);
        scheduleDelayedUiUpdate(() => {
          setReachedBottom("bottomShadowFadeIn");
        }, ANIMATION.BOTTOM_SHADOW_DELAY);
        updatePageConfigByCoupons(legacyCoupons, ReceiveStatus.RECEIVED_SUCCESS, pageConfig?.uiConfig || null);
      } else {
        Toast.info(bindingData?.message || "领取失败，请稍后重试", 2000);
      }
    } catch (error) {
      Toast.info("活动太火爆，请稍后再试", 2000);
    }
  }, 1000, { leading: true, trailing: false });

  useEffect(() => {
    const sid = routerParams.sid;
    if (sid) {
      Cookies.set("sid", sid);
    }
  }, [routerParams.sid]);

  useEffect(() => {
    preloadImages(Object.values(IMAGES.edges));
    document.documentElement.style.setProperty("--listItemCardHeightRatio", `${RATIOS.card.single.height}Px`);
    getPageConfig();
  }, [getPageConfig]);

  useEffect(() => {
    if (pageConfig !== null) {
      const raw = pageConfig.uiConfig?.pageTitle;
      const trimmed = raw != null ? String(raw).trim() : "";
      window.document.title = trimmed || "惊喜优惠券";
      return;
    }
    if (showEmpty) {
      window.document.title = "惊喜优惠券";
    }
  }, [pageConfig, showEmpty]);

  useEffect(() => {
    if (!(showCardView && pageConfig?.cardType === CardType.MULTIPLE)) return;
    const scrollView = document.querySelector(".cardListWrapper");
    if (!scrollView) return;

    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const isAtBottom = Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) <= 1;
      if (isAtBottom && isShowBottomShadow.current) {
        isShowBottomShadow.current = false;
        setReachedBottom("bottomShadowFadeOut");
      } else if (!isAtBottom && !isShowBottomShadow.current) {
        isShowBottomShadow.current = true;
        setReachedBottom("bottomShadowFadeIn");
      }
    };

    scrollView.addEventListener("scroll", handleScroll);
    return () => {
      scrollView.removeEventListener("scroll", handleScroll);
    };
  }, [showCardView, pageConfig?.cardType]);

  /** URL / Cookie 均无 sid 视为未登录：按钮固定用接口下发的领取图，点击走登录 */
  const effectiveSid = routerParams.sid || Cookies.get("sid") || "";

  const mainActionButtonSrc = useMemo(() => {
    const ui = pageConfig?.uiConfig;
    if (!effectiveSid) {
      return ui?.receiveButton || "";
    }
    const src = getFifaMainButtonSrc(ui, pageConfig?.status ?? ReceiveStatus.NOT_RECEIVED);
    return src || "";
  }, [pageConfig?.uiConfig, pageConfig?.status, effectiveSid]);

  return (
    <>
      {!showEmpty ? (
        <div
          className="fifaCouponWrapper"
          id="fifaCouponWrapper"
          style={{
            backgroundColor: `#${pageConfig?.uiConfig?.pageBackgroundDefault || ""}`,
            backgroundImage: `url(${pageConfig?.uiConfig?.pageBackground || ""})`,
          }}
        >
          <div className="contentWrapper">
            <img
              src={pageConfig?.uiConfig?.pageTitleImage}
              alt=""
              className="slogan"
              style={{ height: `${RATIOS.slogan.height}Px` }}
            />
            <div className="machineWrapper">
              <img
                src={pageConfig?.uiConfig?.pageMainImage}
                alt=""
                className="machine"
                style={{ height: `${RATIOS.machine.height}Px` }}
              />
              {pageConfig?.status !== ReceiveStatus.RECEIVED_SUCCESS ? (
                <img
                  src={pageConfig?.uiConfig?.pageGetCoupon}
                  alt=""
                  className={stopGifAnimation ? "gifFadeOut couponGif" : "couponGif"}
                  style={{
                    width: `${RATIOS.couponGif.width}Px`,
                    height: `${RATIOS.couponGif.height}Px`,
                    top: `${RATIOS.machine.cardMarginTop}Px`,
                    left: `${RATIOS.machine.gifMarginLeft}Px`,
                  }}
                />
              ) : null}
              {showCardView && pageConfig?.cardType === CardType.SINGLE ? (
                <div
                  className="singleCardWrapper cardSingleWrapperFadeIn"
                  style={{
                    width: `${RATIOS.card.single.width}Px`,
                    height: `${RATIOS.card.single.height}Px`,
                    top: `${RATIOS.machine.cardMarginTop}Px`,
                    left: `${RATIOS.machine.cardMarginLeft}Px`,
                  }}
                >
                  <img
                    src={IMAGES.edges.topShadow}
                    className="topShadow cardSingleWrapperFadeIn"
                    alt=""
                    style={{ height: `${RATIOS.shadow.top}Px` }}
                  />
                  <div
                    className="cardScrollView singleCardWrapperTransfromDown"
                    style={{ width: `${RATIOS.card.single.width}Px`, height: `${RATIOS.card.single.height}Px` }}
                  >
                    <div className="singleItem">
                      <div className="cardImg" style={{ height: `${RATIOS.card.single.couponHeight}Px` }}>
                        <img src={pageConfig?.singleCard?.couponPic} className="couponPic" alt="" />
                      </div>
                      <img
                        src={IMAGES.edges.bottomJagged}
                        alt=""
                        className="lowerEdge"
                        style={{ width: `${RATIOS.card.single.width}Px`, height: `${RATIOS.card.lowerEdge}Px` }}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
              {showCardView && pageConfig?.cardType === CardType.MULTIPLE ? (
                <div
                  className="cardListContentWrapper"
                  style={{
                    width: `${RATIOS.card.single.width}Px`,
                    height: `${RATIOS.card.single.height}Px`,
                    top: `${RATIOS.machine.cardMarginTop}Px`,
                    left: `${RATIOS.machine.cardMarginLeft}Px`,
                  }}
                >
                  <img
                    src={IMAGES.edges.topShadow}
                    className="topShadow cardListWrapperFadeIn"
                    alt=""
                    style={{ height: `${RATIOS.shadow.top}Px` }}
                  />
                  <div className="cardListWrapper cardListWrapperFadeIn">
                    <div
                      className="cardScrollView"
                      style={{ width: `${RATIOS.card.single.width}Px`, height: `${RATIOS.card.single.height}Px` }}
                    >
                      <div className="innerWrapper cardListWrapperTransfromDown">
                        {pageConfig?.multipleCards?.map((item: FifaCouponCard, index: number) => (
                          <div className="listItem" key={index}>
                            {index !== 0 ? (
                              <img
                                src={IMAGES.edges.topJagged}
                                alt=""
                                className="upperEdge"
                                style={{ width: `${RATIOS.card.single.width}Px`, height: `${RATIOS.card.lowerEdge}Px` }}
                              />
                            ) : null}
                            <div
                              className="content"
                              style={{ width: `${RATIOS.card.single.width}Px`, height: `${RATIOS.card.listItem.height}Px` }}
                            >
                              <div className="couponImg">
                                <img src={item.couponLogo} alt="" className="couponImg" />
                              </div>
                            </div>
                            <img
                              src={IMAGES.edges.bottomJagged}
                              alt=""
                              className="lowerEdge"
                              style={{ width: `${RATIOS.card.single.width}Px`, height: `${RATIOS.card.lowerEdge}Px` }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {showCardView ? (
                    <div className={`bottomShadow ${reachedBottom}`}>
                      <img src={IMAGES.edges.bottomShadow} alt="" style={{ height: `${RATIOS.shadow.bottom}Px` }} />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            {mainActionButtonSrc ? (
              <img
                src={mainActionButtonSrc}
                alt=""
                onClick={effectiveSid ? receiveCoupon : toLogin}
                className="actionButton"
                style={{ height: `${RATIOS.button.height}Px` }}
              />
            ) : null}
            {pageConfig?.uiConfig?.ruleDesc ? (
              <div className="ruleContent">
                <div className="title">领取说明</div>
                <div className="content" dangerouslySetInnerHTML={{ __html: `${pageConfig?.uiConfig?.ruleDesc}` }} />
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="fifaEmptyWrapper">
          <img
            src="https://img.mcd.cn/mini/main/images/invoice_icon_list_empty.png"
            alt=""
            style={{ marginTop: `${RATIOS.empty.pageTop}px` }}
          />
          <div className="emptyText">抱歉,页面加载失败了...</div>
          <div className="emptySubText">请稍后再尝试吧</div>
          <div className="retryBtn" onClick={getPageConfig}>重试加载</div>
        </div>
      )}
    </>
  );
};

export default FifaGetCoupon;

